import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query'
import { InferRequestType, InferResponseType } from 'hono/client'

import { apiClient, HcFetchError } from '@/apiClient.ts'

type UpdateDocumentByIdRequest = InferRequestType<
  (typeof apiClient.documents)[':documentId']['$patch']
>['json']
type UpdateDocumentByIdResponse = InferResponseType<
  (typeof apiClient.documents)[':documentId']['$patch']
>

export type UseUpdateDocumentByIdArgs = {
  documentId: string
} & UpdateDocumentByIdRequest

export const useUpdateDocumentById = (
  options?: UseMutationOptions<
    UpdateDocumentByIdResponse,
    Error,
    UseUpdateDocumentByIdArgs
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (variables) => {
      return await apiClient.documents[':documentId']
        .$patch({
          param: { documentId: variables.documentId },
          json: {
            document: variables.document,
            attributes: variables.attributes,
          },
        })
        .then(async (r) => {
          if (r.ok) return r.json()

          throw new HcFetchError(r.status, await r.json())
        })
    },
    mutationKey: ['document-update'],
    onSuccess: (data, variables, context) => {
      // Invalidate logs query to refresh the logs list
      queryClient.invalidateQueries({ queryKey: ['logs'] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}
